/**
 * App-level error boundary. Wraps routed content so a render-time exception
 * shows a friendly fallback instead of unmounting to a blank page (which is
 * what Add-Class did before the fix — a useEffect throw silently unwound the
 * tree and left the user staring at a half-rendered shell).
 *
 * Sentry capture is lazy + DSN-gated so this is safe to ship before Sentry
 * is wired up; the fallback UI works regardless.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { fontSize } from "@utils/tokens";

interface Props {
  children: ReactNode;
  /** Optional override of the fallback render */
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info);
    }

    // Stale-chunk recovery: after a Vercel deploy the user's cached
    // index.html still points at hashed chunks that no longer exist, so
    // the next lazy import fails with one of several browser-specific
    // messages. Reload once to fetch the new index.html + chunks. A
    // sessionStorage timestamp guards against a reload loop if the new
    // build is ALSO broken — we only auto-reload once per 10 seconds.
    // See SENTRY-COPPER-ELEPHANT-9 / -S / -D.
    const msg = String(error?.message ?? '');
    const isStaleChunk =
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /is not a valid JavaScript MIME type/i.test(msg);
    if (isStaleChunk) {
      try {
        const KEY = 'yudu_stale_chunk_reloaded_at';
        const last = Number(window.sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last > 10_000) {
          window.sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage can throw in private mode / iframe sandboxes —
        // fall through to the regular fallback UI in that case.
      }
    }

    // Fire-and-forget Sentry capture if available
    if (import.meta.env.VITE_SENTRY_DSN) {
      void import('@sentry/react').then((Sentry) => {
        try {
          Sentry.withScope((scope) => {
            scope.setTag('boundary', 'app');
            scope.setContext('react', { componentStack: info.componentStack });
            Sentry.captureException(error);
          });
        } catch {
          // never let monitoring break the fallback
        }
      }).catch(() => {});
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh" px={3}>
        <Stack spacing={2} alignItems="center" maxWidth={520}>
          <Typography variant="h5">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            The page hit an unexpected error and could not finish loading. The team has been notified automatically.
            You can try reloading, or go back to the dashboard.
          </Typography>
          {import.meta.env.DEV && (
            <Box
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: fontSize.xs,
                whiteSpace: 'pre-wrap',
                bgcolor: 'background.default',
                p: 2,
                borderRadius: 1,
                width: '100%',
                maxHeight: 240,
                overflow: 'auto',
              }}
            >
              {String(this.state.error?.stack ?? this.state.error)}
            </Box>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
            <Button variant="outlined" onClick={this.reset}>Try again</Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
